import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { toast } from 'sonner';
import { supabase } from '../utils/supabase/client';
import { fetchWithAuth } from '../utils/api';
import { API_BASE } from '../utils/supabase/info';
import { CURRENT_TERMS_VERSION } from '../utils/terms-version';
import { devLog, devError, devWarn } from '../utils/logger';

function isAbortLikeError(error: any): boolean {
  const message = (error?.message || '').toString().toLowerCase();
  return (
    error?.name === 'AbortError' ||
    message.includes('abort') ||
    message.includes('aborted')
  );
}

function isTransientAuthOrNetworkError(error: any): boolean {
  const message = (error?.message || '').toString().toLowerCase();
  return (
    isAbortLikeError(error) ||
    error?.name === 'TimeoutError' ||
    message.includes('timeout') ||
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('load failed') ||
    message.includes('connection') ||
    message.includes('temporary')
  );
}

async function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: any = null;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timer = setTimeout(() => {
      const err: any = new Error(`${label} timed out after ${ms}ms`);
      err.name = 'TimeoutError';
      reject(err);
    }, ms);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

type FetchUserProfileOptions = {
  navigateTo?: (view: string) => void;
  setCurrentView?: Dispatch<SetStateAction<any>>;
  setCurrentTermsVersion?: Dispatch<SetStateAction<string>>;
  setNeedsTermsUpdate?: Dispatch<SetStateAction<boolean>>;
  setIsFirstLogin?: Dispatch<SetStateAction<boolean>>;
  handlingProfile401Ref?: MutableRefObject<boolean>;
};

type AuthContextValue = {
  session: any;
  setSession: Dispatch<SetStateAction<any>>;
  userProfile: any;
  setUserProfile: Dispatch<SetStateAction<any>>;
  isAdmin: boolean;
  setIsAdmin: Dispatch<SetStateAction<boolean>>;
  isAdminCheckComplete: boolean;
  setIsAdminCheckComplete: Dispatch<SetStateAction<boolean>>;
  blockedUserIds: string[];
  setBlockedUserIds: Dispatch<SetStateAction<string[]>>;
  handleSignOut: () => Promise<void>;
  fetchUserProfile: (sessionOverride?: any, options?: FetchUserProfileOptions) => Promise<any>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminCheckComplete, setIsAdminCheckComplete] = useState(false);
  const [blockedUserIds, setBlockedUserIds] = useState<string[]>([]);
  const userProfileRef = useRef<any>(null);

  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile]);

  const handleSignOut = useCallback(async () => {
    // Never let signOut hang the UI (Android/mobile networks can stall).
    try {
      await withTimeout(supabase.auth.signOut(), 8000, 'supabase.auth.signOut');
    } catch (error) {
      devWarn('Sign out failed/timeout (continuing with local cleanup):', error);
    }
    setSession(null);
    setUserProfile(null);
    setIsAdmin(false);
    setIsAdminCheckComplete(true);
    setBlockedUserIds([]);
    localStorage.removeItem('talentfunn_is_admin');
    // Ensure local auth is cleared even if Supabase signOut stalls.
    try {
      localStorage.removeItem('talentfunn-auth');
      localStorage.removeItem('talentfunn-auth-backup');
    } catch {
      // ignore
    }
  }, []);

  const fetchUserProfile = useCallback(
    async (sessionOverride?: any, options: FetchUserProfileOptions = {}): Promise<any> => {
      const currentSession = sessionOverride || session;

      if (!currentSession) {
        setIsAdminCheckComplete(true);
        return null;
      }

      devLog('Fetching user profile...');

      try {
        const { data: userData, error: userError } = await withTimeout(
          supabase.auth.getUser(),
          8000,
          'supabase.auth.getUser'
        );

        if (userError || !userData.user) {
          if (isTransientAuthOrNetworkError(userError)) {
            devWarn('Could not verify user due to transient error - preserving session:', userError);
            return null;
          }
          devError('Could not verify user - logging out');
          await handleSignOut();
          return null;
        }

        if (!userData.user.email_confirmed_at) {
          devWarn('Email not confirmed - logging out');
          await handleSignOut();
          toast.error('E-post ma bekreftes for du kan logge inn. Sjekk innboksen din.');
          options.navigateTo?.('auth');
          return null;
        }
      } catch (verifyError) {
        if (isTransientAuthOrNetworkError(verifyError)) {
          devWarn('Email verification check transiently failed - preserving session:', verifyError);
          return null;
        }
        devError('Email verification check failed:', verifyError);
        await handleSignOut();
        return null;
      }

      try {
        const {
          data: { session: latestSession },
        } = await withTimeout(supabase.auth.getSession(), 8000, 'supabase.auth.getSession');
        const accessToken = latestSession?.access_token || currentSession.access_token;
        if (!accessToken) {
          devError('No access token available for user-profile fetch');
          await handleSignOut();
          return null;
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 12000);
        let response: Response;
        try {
          response = await fetch(`${API_BASE}/user-profile`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            signal: controller.signal,
          });
        } catch (error) {
          if (isTransientAuthOrNetworkError(error)) {
            devWarn('User profile fetch transiently failed - preserving session:', error);
          } else {
            devError('User profile fetch failed:', error);
          }
          return null;
        } finally {
          clearTimeout(timeoutId);
        }

        if (response.ok) {
          const data = await response.json();
          devLog('User profile fetched:', {
            userType: data.userType,
            subscription: data.subscription,
            messagingAccess: data.messagingAccess,
            isAdmin: data.isAdmin,
          });

          if (!userProfileRef.current && data && !data.isAdmin) {
            options.setIsFirstLogin?.(true);
          }

          setUserProfile(data);
          userProfileRef.current = data;
          setIsAdmin(data.isAdmin === true);

          if (data.isAdmin === true) {
            devLog('Admin user detected - setting admin view...');
            options.setCurrentView?.('admin');
            localStorage.setItem('talentfunn_is_admin', 'true');
          } else {
            localStorage.removeItem('talentfunn_is_admin');
          }

          const userTermsVersion = data.termsVersion || '1.0.0';
          options.setCurrentTermsVersion?.(userTermsVersion);

          const shouldShowTermsUpdate = userTermsVersion !== CURRENT_TERMS_VERSION;
          options.setNeedsTermsUpdate?.(shouldShowTermsUpdate);

          try {
            const blockedResponse = await fetchWithAuth(`${API_BASE}/blocked`);

            if (blockedResponse.ok) {
              const blockedData = await blockedResponse.json();
              const blockedIds = blockedData.blockedUsers?.map((user: any) => user.id) || [];
              const blockersResponse = await fetchWithAuth(`${API_BASE}/blockers`);

              if (blockersResponse.ok) {
                const blockersData = await blockersResponse.json();
                const blockerIds = blockersData.blockers?.map((user: any) => user.id) || [];
                const allBlockedIds = [...new Set([...blockedIds, ...blockerIds])];
                setBlockedUserIds(allBlockedIds);
                devLog('Blocked users loaded:', {
                  iBlocked: blockedIds.length,
                  blockedMe: blockerIds.length,
                  total: allBlockedIds.length,
                });
              } else {
                setBlockedUserIds(blockedIds);
                devLog('Blocked users loaded (no blockers):', blockedIds.length);
              }
            }
          } catch (blockedError) {
            devLog('Error fetching blocked users:', blockedError);
          }

          return data;
        }

        if (response.status === 401 || response.status === 403) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

          if (errorData.code === 'EMAIL_NOT_VERIFIED') {
            devError('Email not verified - forcing logout...');
            await handleSignOut();
            options.navigateTo?.('auth');
            toast.error('E-post ma bekreftes for du kan logge inn. Sjekk innboksen din.');
          } else {
            const shouldDeduplicate = !!options.handlingProfile401Ref;
            if (shouldDeduplicate && options.handlingProfile401Ref?.current) return null;
            if (shouldDeduplicate && options.handlingProfile401Ref) {
              options.handlingProfile401Ref.current = true;
            }
            devError('User unauthorized (possibly deleted) - forcing logout...');
            await handleSignOut();
            options.navigateTo?.('home');
            toast.error('Din konto er ikke lenger aktiv. Vennligst kontakt support hvis dette er en feil.');
            if (shouldDeduplicate && options.handlingProfile401Ref) {
              setTimeout(() => {
                options.handlingProfile401Ref!.current = false;
              }, 3000);
            }
          }
          return null;
        }

        devError('Failed to fetch user profile:', response.status);
        return null;
      } catch (error) {
        if (isTransientAuthOrNetworkError(error)) {
          devWarn('Error fetching user profile (transient) - preserving session:', error);
        } else {
          devLog('Error fetching user profile:', error);
        }
        return null;
      } finally {
        devLog('Admin check complete - desktop menu should now be visible');
        setIsAdminCheckComplete(true);
      }
    },
    [session, handleSignOut]
  );

  return (
    <AuthContext.Provider
      value={{
        session,
        setSession,
        userProfile,
        setUserProfile,
        isAdmin,
        setIsAdmin,
        isAdminCheckComplete,
        setIsAdminCheckComplete,
        blockedUserIds,
        setBlockedUserIds,
        handleSignOut,
        fetchUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
